import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['input', 'hidden', 'dropdown', 'chips', 'template'];
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    multiple: { type: Boolean, default: false },
    name: String,
  };

  connect() {
    this.debouncedSearch = this.debounce(this.search.bind(this), 200);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  onDocumentClick = (event) => {
    if (!this.element.contains(event.target)) {
      this.dropdownTarget.innerHTML = '';
    }
  };

  onInput() {
    if (this.inputTarget.value.trim().length < this.minLengthValue) {
      this.dropdownTarget.innerHTML = '';
      return;
    }
    this.debouncedSearch();
  }

  async search() {
    const url = `${this.urlValue}?q=${encodeURIComponent(this.inputTarget.value)}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    const json = await response.json();
    this.renderResults(json.results || []);
  }

  renderResults(results) {
    const filtered = this.multipleValue
      ? results.filter((r) => !this.selectedIds().has(String(r.id)))
      : results;

    this.dropdownTarget.innerHTML = filtered
      .map(
        ({ id, text }) =>
          `<button type="button" data-action="click->async-search#select" data-id="${id}" data-text="${this.escapeHtml(text)}" class="block w-full text-left px-12 py-8 hover:bg-black-50 text-paragraph-m">${this.escapeHtml(text)}</button>`,
      )
      .join('');
  }

  select(event) {
    const { id, text } = event.currentTarget.dataset;

    if (this.multipleValue) {
      this.addChip(id, text);
      this.inputTarget.value = '';
    } else {
      this.hiddenTarget.value = id;
      this.inputTarget.value = text;
    }

    this.dropdownTarget.innerHTML = '';
  }

  addChip(id, text) {
    const fragment = this.templateTarget.content.cloneNode(true);
    const chip = fragment.firstElementChild;
    chip.dataset.id = id;
    chip.querySelector('[data-chip-label]').textContent = text;
    const hidden = chip.querySelector('[data-chip-input]');
    hidden.name = this.nameValue;
    hidden.value = id;
    this.chipsTarget.appendChild(chip);
  }

  removeChip(event) {
    event.preventDefault();
    const chip = event.currentTarget.closest('[data-id]');
    chip?.remove();
  }

  selectedIds() {
    const ids = new Set();
    if (this.hasChipsTarget) {
      this.chipsTarget.querySelectorAll('[data-id]').forEach((c) => ids.add(c.dataset.id));
    }
    return ids;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }
}
